import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  House, BookOpen, ShareNetwork, Funnel, Images, Robot, Upload, ChartLine, Gear,
  SignOut, Microphone, Plus, List, CaretLeft, CaretRight, User
} from "@phosphor-icons/react";

// Pages
import HomePage from "./pages/HomePage";
import LessonStudio from "./pages/LessonStudio";
import SocialStudio from "./pages/SocialStudio";
import FunnelBuilder from "./pages/FunnelBuilder";
import MediaLibrary from "./pages/MediaLibrary";
import AIAgents from "./pages/AIAgents";
import AcademyPublisher from "./pages/AcademyPublisher";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import LoginPage from "./pages/LoginPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.access_token);
    setToken(res.data.access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("token", res.data.access_token);
    setToken(res.data.access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Sidebar Component
const Sidebar = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { icon: House, label: "Home", path: "/" },
    { icon: BookOpen, label: "Lessons", path: "/lessons" },
    { icon: ShareNetwork, label: "Social", path: "/social" },
    { icon: Funnel, label: "Funnels", path: "/funnels" },
    { icon: Images, label: "Media", path: "/media" },
    { icon: Robot, label: "Agents", path: "/agents" },
    { icon: Upload, label: "Publish", path: "/publisher" },
    { icon: ChartLine, label: "Analytics", path: "/analytics" },
    { icon: Gear, label: "Settings", path: "/settings" },
  ];

  // Mobile bottom navigation
  if (isMobile) {
    return (
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-900/95 backdrop-blur-lg border-t border-white/10 px-2 py-2 safe-area-pb">
        <div className="flex justify-around items-center">
          {navItems.slice(0, 5).map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== "/" && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${
                  isActive ? 'text-amber-500' : 'text-zinc-400'
                }`}
              >
                <Icon size={24} weight={isActive ? "fill" : "regular"} />
                <span className="text-[10px]">{item.label}</span>
              </button>
            );
          })}
          <button
            onClick={() => navigate('/settings')}
            data-testid="nav-more-mobile"
            className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${
              location.pathname === '/settings' ? 'text-amber-500' : 'text-zinc-400'
            }`}
          >
            <List size={24} />
            <span className="text-[10px]">More</span>
          </button>
        </div>
      </nav>
    );
  }

  return (
    <aside className={`sidebar fixed left-0 top-0 h-screen flex flex-col transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}>
      {/* Logo */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          {collapsed ? (
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-command-hub-48/artifacts/opkezy5x_CB3D4111-0127-441A-AEB4-FB146D6CBB1E.PNG" 
              alt="IPVALUE"
              className="w-10 h-10 object-contain"
            />
          ) : (
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-command-hub-48/artifacts/opkezy5x_CB3D4111-0127-441A-AEB4-FB146D6CBB1E.PNG" 
              alt="IPVALUE Managements"
              className="h-14 w-auto object-contain"
            />
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== "/" && location.pathname.startsWith(item.path));
          const Icon = item.icon;
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`nav-item w-full flex items-center gap-3 px-6 py-3 text-left transition-colors duration-200
                ${isActive ? 'active text-white bg-white/5' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}
            >
              <Icon size={22} weight={isActive ? "fill" : "regular"} />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
            <User size={20} className="text-zinc-400" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          )}
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
          >
            <SignOut size={20} />
          </button>
        </div>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:bg-zinc-700 transition-colors"
      >
        {collapsed ? <CaretRight size={14} /> : <CaretLeft size={14} />}
      </button>
    </aside>
  );
};

// Voice Command Button
const VoiceCommandButton = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleVoiceCommand = async () => {
    // Check browser support
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Voice commands not supported in this browser. Try Chrome or Edge.");
      return;
    }

    // Request microphone permission first
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (permissionError) {
      toast.error("Please allow microphone access to use voice commands");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    setIsListening(true);
    toast.info("Listening... Speak your command");

    recognition.onresult = async (event) => {
      const command = event.results[0][0].transcript;
      setTranscript(command);
      
      // Only process final results
      if (event.results[0].isFinal) {
        setIsListening(false);
        toast.success(`You said: "${command}"`);
        
        try {
          const res = await axios.post(`${API}/voice-command`, null, {
            params: { command }
          });
          console.log("Voice command result:", res.data);
        } catch (err) {
          console.error("Voice command API error:", err);
        }
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      console.error("Speech recognition error:", event.error);
      
      switch(event.error) {
        case 'not-allowed':
          toast.error("Microphone access denied. Please allow in browser settings.");
          break;
        case 'no-speech':
          toast.info("No speech detected. Try again and speak clearly.");
          break;
        case 'network':
          toast.error("Network error. Check your internet connection.");
          break;
        case 'aborted':
          toast.info("Voice command cancelled");
          break;
        default:
          toast.error(`Voice error: ${event.error}. Try again.`);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    try {
      recognition.start();
    } catch (startError) {
      setIsListening(false);
      toast.error("Could not start voice recognition. Please try again.");
      console.error("Recognition start error:", startError);
    }
  };

  return (
    <>
      <button
        onClick={handleVoiceCommand}
        data-testid="voice-command-btn"
        className={`voice-btn-pulse fixed z-40 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 text-black flex items-center justify-center shadow-lg hover:scale-105 transition-transform duration-200 ${
          isMobile ? 'bottom-20 right-4 w-14 h-14' : 'bottom-8 right-8 w-16 h-16'
        }`}
      >
        <Microphone size={isMobile ? 24 : 28} weight="fill" />
      </button>

      <AnimatePresence>
        {isListening && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="voice-overlay"
            onClick={() => setIsListening(false)}
          >
            <div className="text-center">
              <div className="voice-waveform mb-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="voice-bar" />
                ))}
              </div>
              <p className="text-2xl text-white mb-2 font-semibold">🎤 Listening...</p>
              <p className="text-zinc-400 mb-4">Speak your command clearly</p>
              {transcript && (
                <div className="bg-zinc-800/50 rounded-xl p-4 max-w-md mx-auto">
                  <p className="text-amber-400 text-lg">"{transcript}"</p>
                </div>
              )}
              <p className="text-zinc-500 text-sm mt-6">Tap anywhere to cancel</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Main Layout
const MainLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex min-h-screen bg-zinc-950">
      <div className="noise-overlay" />
      <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      <main className={`main-content transition-all duration-300 ${isMobile ? 'ml-0 pb-24' : sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        <div className={`${isMobile ? 'p-4' : 'p-8'}`}>
          {children}
        </div>
      </main>
      <VoiceCommandButton />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: 'hsl(240 6% 10%)',
              border: '1px solid hsl(240 5% 20%)',
              color: 'white',
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/lessons" element={<LessonStudio />} />
                    <Route path="/social" element={<SocialStudio />} />
                    <Route path="/funnels" element={<FunnelBuilder />} />
                    <Route path="/media" element={<MediaLibrary />} />
                    <Route path="/agents" element={<AIAgents />} />
                    <Route path="/publisher" element={<AcademyPublisher />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/settings" element={<Settings />} />
                  </Routes>
                </MainLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
